import { useState } from "react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { UserCheck, UserX, Ban, Clock, RefreshCw, ShieldCheck } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending_approval: { label: "Pending", color: "bg-amber-100 text-amber-700 border-amber-200", icon: Clock },
  approved: { label: "Approved", color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: ShieldCheck },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-700 border-red-200", icon: UserX },
  suspended: { label: "Suspended", color: "bg-slate-100 text-slate-600 border-slate-200", icon: Ban },
};

export function UserApprovalPanel({ users, onRefresh }) {
  const [actionLoading, setActionLoading] = useState(null);

  const pendingUsers = users.filter(u => u.status === "pending_approval");
  const otherUsers = users.filter(u => u.status && u.status !== "pending_approval" && u.status !== "approved");

  const handleStatusChange = async (userId, newStatus) => {
    setActionLoading(`${userId}-${newStatus}`);
    try {
      await axios.put(`${API}/admin/users/${userId}/status`, { status: newStatus });
      onRefresh();
    } catch (e) {
      console.error("Status update failed:", e);
    }
    setActionLoading(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "\u2014";
    return new Date(dateStr).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  };

  const renderUserRow = (u) => {
    const config = STATUS_CONFIG[u.status] || STATUS_CONFIG.pending_approval;
    const StatusIcon = config.icon;
    return (
      <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`approval-row-${u.id}`}>
        <td className="py-3 px-4">
          <p className="font-medium text-[#0B1F3B] text-sm">{u.name}</p>
          <p className="text-xs text-slate-400">{u.email}</p>
        </td>
        <td className="py-3 px-4 text-sm text-slate-500 capitalize">{u.role}</td>
        <td className="py-3 px-4 text-sm text-slate-500">{formatDate(u.created_at)}</td>
        <td className="py-3 px-4">
          <Badge className={`${config.color} text-xs border`}>
            <StatusIcon className="w-3 h-3 mr-1" />
            {config.label}
          </Badge>
        </td>
        <td className="py-3 px-4 text-right">
          <div className="flex items-center justify-end gap-1.5">
            {u.status !== "approved" && (
              <Button
                size="sm"
                className="h-7 px-2.5 bg-emerald-600 hover:bg-emerald-700 text-xs text-white"
                onClick={() => handleStatusChange(u.id, "approved")}
                disabled={actionLoading === `${u.id}-approved`}
                data-testid={`approve-user-${u.id}`}
              >
                <UserCheck className="w-3.5 h-3.5 mr-1" />
                Approve
              </Button>
            )}
            {u.status !== "rejected" && u.status !== "approved" && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2.5 text-xs border-red-200 text-red-600 hover:bg-red-50"
                onClick={() => handleStatusChange(u.id, "rejected")}
                disabled={actionLoading === `${u.id}-rejected`}
                data-testid={`reject-user-${u.id}`}
              >
                <UserX className="w-3.5 h-3.5 mr-1" />
                Reject
              </Button>
            )}
            {u.status === "approved" && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2.5 text-xs border-slate-200 text-slate-600 hover:bg-slate-50"
                onClick={() => handleStatusChange(u.id, "suspended")}
                disabled={actionLoading === `${u.id}-suspended`}
                data-testid={`suspend-user-${u.id}`}
              >
                <Ban className="w-3.5 h-3.5 mr-1" />
                Suspend
              </Button>
            )}
            {(u.status === "rejected" || u.status === "suspended") && (
              <Button
                size="sm"
                className="h-7 px-2.5 bg-emerald-600 hover:bg-emerald-700 text-xs text-white"
                onClick={() => handleStatusChange(u.id, "approved")}
                disabled={actionLoading === `${u.id}-approved`}
                data-testid={`activate-user-${u.id}`}
              >
                <ShieldCheck className="w-3.5 h-3.5 mr-1" />
                Activate
              </Button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-6">
      {/* Pending Approvals */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-[#0B1F3B]">Pending Approval Requests</h3>
            {pendingUsers.length > 0 && (
              <Badge className="bg-amber-100 text-amber-700 border border-amber-200 text-xs">{pendingUsers.length}</Badge>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={onRefresh} className="text-xs text-slate-500" data-testid="refresh-approvals-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>
        <Card className="premium-card">
          <CardContent className="p-0">
            {pendingUsers.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">User</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Role</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Registered</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingUsers.map(renderUserRow)}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-10">
                <UserCheck className="w-10 h-10 text-emerald-200 mx-auto mb-3" />
                <p className="text-slate-400 text-sm">No pending approval requests</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Rejected / Suspended */}
      {otherUsers.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-[#0B1F3B] mb-3">Rejected / Suspended Users</h3>
          <Card className="premium-card">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">User</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Role</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Registered</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {otherUsers.map(renderUserRow)}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
